import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Create the base theme
let theme = createTheme({
  // Define custom breakpoints for more precise control
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: '#ED7420', // From the original CSS variables
      light: '#ff9a52',
      dark: '#c85000',
    },
    secondary: {
      main: '#3A506B',
      light: '#5d748e',
      dark: '#2c3e5a',
    },
    error: {
      main: '#E74C3C',
    },
    warning: {
      main: '#F39C12',
    },
    info: {
      main: '#3498DB',
    },
    success: {
      main: '#2ECC71',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Segoe UI", sans-serif',
    // Base typography for mobile screens first
    h1: {
      fontSize: '1.75rem', // Smaller on mobile
      fontWeight: 600,
      color: '#ED7420',
      '@media (min-width:600px)': {
        fontSize: '2rem', // Larger on desktop
      },
    },
    h2: {
      fontSize: '1.35rem', // Smaller on mobile
      fontWeight: 600,
      color: '#3A506B',
      '@media (min-width:600px)': {
        fontSize: '1.5rem', // Larger on desktop
      },
    },
    h3: {
      fontSize: '1.1rem', // Smaller on mobile
      fontWeight: 600,
      color: '#3A506B',
      '@media (min-width:600px)': {
        fontSize: '1.2rem', // Larger on desktop
      },
    },
    subtitle1: {
      color: '#708181',
      fontSize: '0.9rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    // Make body text more readable on mobile
    body1: {
      fontSize: '0.95rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '0.85rem',
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          padding: '16px', // Smaller padding on mobile
          marginBottom: '24px',
          '@media (min-width:600px)': {
            padding: '24px', // Medium padding on tablets
          },
          '@media (min-width:900px)': {
            padding: '32px', // Larger padding on desktop
            marginBottom: '32px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square buttons like Magna
          textTransform: 'uppercase',
          fontWeight: 500,
          letterSpacing: '0.5px',
          padding: '10px 16px', // Smaller padding on mobile
          minWidth: '100px', // Smaller min-width on mobile
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          fontSize: '0.875rem', // Smaller font on mobile
          '&:focus': {
            outline: 0,
          },
          '&.Mui-disabled': {
            opacity: 0.65,
            '&::before': {
              display: 'none',
            }
          },
          '@media (min-width:600px)': {
            padding: '12px 24px', // Regular padding on desktop
            minWidth: '120px', // Regular min-width on desktop
            fontSize: '0.9375rem', // Regular font size on desktop
          },
          // Make touch targets larger on mobile
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        },
        // Primary button - "Schedule a Deposition" style
        containedPrimary: {
          backgroundColor: '#ED7420 !important',
          color: '#FFFFFF !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#ff6a00 !important',
            backgroundColor: '#fff !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#ff6a00 !important',
            backgroundColor: '#fff !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Secondary button - "Retrieve a Record" style
        containedSecondary: {
          backgroundColor: 'transparent !important',
          color: '#ED7420 !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Outlined primary - Secondary button style
        outlined: {
          borderWidth: '1px',
          padding: '12px 24px',
        },
        outlinedPrimary: {
          backgroundColor: 'transparent !important',
          color: '#ED7420 !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Size variants
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.875rem',
          minWidth: 'auto',
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.125rem',
        },
        // For icon buttons
        startIcon: {
          marginRight: '8px',
        },
        endIcon: {
          marginLeft: '8px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '@media (min-width:600px)': {
            marginBottom: '24px',
          },
          // Improve input field height for touch targets
          '& .MuiInputBase-root': {
            minHeight: '48px', // Larger touch target on mobile
          },
          // Improve input text size on mobile
          '& .MuiInputBase-input': {
            fontSize: '1rem', // Larger text for readability
            padding: '12px 14px', // Slightly more padding
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '@media (min-width:600px)': {
            marginBottom: '24px',
          },
        },
      },
    },
    // Improve Select dropdown touch-friendliness
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: '1.4375em',
          padding: '12px 14px', // Larger touch target
          '&.MuiSelect-select': {
            minHeight: '1.4375em',
          },
        },
      },
    },
    // Improve checkboxes and radio buttons for touch
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#ED7420',
          padding: '9px', // Larger touch target
          '@media (min-width:600px)': {
            padding: '9px', // Standard padding on desktop
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '9px', // Larger touch target
          '@media (min-width:600px)': {
            padding: '9px', // Standard padding on desktop
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 54,
          height: 28,
          padding: 0,
        },
        switchBase: {
          padding: 4,
          '&.Mui-checked': {
            transform: 'translateX(26px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#ED7420',
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
        },
        track: {
          borderRadius: 14,
          opacity: 1,
          backgroundColor: '#ccc',
        },
      },
    },
  },
});

// Apply responsive font sizes
theme = responsiveFontSizes(theme);

export default theme;